import { IS_ALPHA_STAGE } from "../api-config.mjs";

export const SESSION_COOKIE_NAME = IS_ALPHA_STAGE ? "sid_alpha" : "sid";

// Stage-specific cookie domain. Mirrors what cca/user.py (the backend
// /a2/login handler) writes in its Set-Cookie response: Domain=
// alpha.earningscall.biz on alpha, Domain=earningscall.biz on prod. We
// need this on the client because preview envs (e.g., brock.alpha.
// earningscall.biz) make the login POST cross-origin to alpha.
// earningscall.biz/a2/login — and axios defaults to withCredentials=false,
// so the browser ignores Set-Cookie on cross-origin responses without
// credentials. Mirroring the cookie from the response body's session.session_id
// covers that gap without requiring the legacy api to allowlist every
// preview subdomain for CORS-with-credentials.
const SESSION_COOKIE_DOMAIN = IS_ALPHA_STAGE
  ? "alpha.earningscall.biz"
  : "earningscall.biz";

export const isBrowser = () => {
    // During nodejs build time, localStorage doesn't exist.
    if (typeof localStorage === 'undefined') {
      return false;
    }
    return true;
};

// https://www.tabnine.com/academy/javascript/how-to-set-cookies-javascript/
export const setCookie = (cName, cValue, expDays) => {
  let date = new Date();
  date.setTime(date.getTime() + (expDays * 24 * 60 * 60 * 1000));
  const expires = `expires=${date.toUTCString()}`;
  document.cookie = `${cName}=${cValue}; ${expires}; path=/`;
};

// Writes the stage-specific session cookie (`sid_alpha` / `sid`) so the
// api's AuthorizationFilter can authenticate cross-host sub-resource
// requests — `<audio src="https://media.…/audio?…">` and
// `fetch("https://media.…/transcript?…")`. The browser auto-attaches
// this cookie because Domain=alpha.earningscall.biz covers both the page
// host (brock.alpha.earningscall.biz, alpha.earningscall.biz, …) and the
// api host (media.alpha.earningscall.biz). SameSite=Strict matches what
// the backend writes — same-site sub-resource requests (under the
// earningscall.biz eTLD+1) still attach the cookie; cross-site fetches
// don't.
//
// Skipped on localhost: Domain=alpha.earningscall.biz wouldn't match the
// document's origin, the browser would reject the cookie, and local dev
// already authenticates via the ?token= query-param fallback.
export const setSessionCookie = (sessionId) => {
  if (!isBrowser()) return;
  if (!sessionId) return;
  if (typeof window === 'undefined') return;
  if (window.location.hostname === 'localhost') return;
  const date = new Date();
  date.setTime(date.getTime() + 24 * 60 * 60 * 1000);
  const expires = `expires=${date.toUTCString()}`;
  document.cookie =
    `${SESSION_COOKIE_NAME}=${sessionId}; ${expires}; ` +
    `path=/; domain=${SESSION_COOKIE_DOMAIN}; SameSite=Strict; Secure`;
};

// Counterpart for logout. The plain deleteCookie() in this file only
// clears a host-only cookie — it can't reach the Domain=alpha.
// earningscall.biz cookie that setSessionCookie() (or cca/user.py) wrote.
// Setting an expired cookie with the same Domain is the only way to
// actually remove it.
export const clearSessionCookie = () => {
  if (!isBrowser()) return;
  if (typeof window === 'undefined') return;
  if (window.location.hostname === 'localhost') return;
  document.cookie =
    `${SESSION_COOKIE_NAME}=; ` +
    `expires=Thu, 01 Jan 1970 00:00:00 GMT; ` +
    `path=/; domain=${SESSION_COOKIE_DOMAIN}; SameSite=Strict; Secure`;
};

export function getCookie(cname) {
  let name = cname + "=";
  let decodedCookie = decodeURIComponent(document.cookie);
  let ca = decodedCookie.split(';');
  for(let i = 0; i <ca.length; i++) {
    let c = ca[i];
    while (c.charAt(0) === ' ') {
      c = c.substring(1);
    }
    if (c.indexOf(name) === 0) {
      return c.substring(name.length, c.length);
    }
  }
  return "";
};

export function deleteCookie(cname) {
  setCookie(cname, getCookie(cname), 0);
};

export const isMobileDevice = () => {
  if (isBrowser()) {
    return window.innerWidth < 991;
  }
  return true;
};
